import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { Text, Button, Input } from '@nula/core-ui';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import { isDesktop } from '@/lib/platform';
import { events } from '@/lib/platform/generated/bindings.gen';
import { useEffect, useRef, useState } from 'react';
import {
  authRedirectUri,
  exchangeCodeForTokens,
  fetchUserInfo,
  parseCallback,
  startAuth,
  startEmailPasswordless,
  verifyEmailOtp,
  type PendingAuth,
  type OAuthTokens,
} from '@/lib/auth/auth0';
import { mailProviderFor, type MailProvider } from '@/lib/auth/mail-provider';
import {
  getLastEmail,
  setAccessTokenOnService,
  storeLastEmail,
  storeRefreshToken,
} from '@/lib/auth/token-storage';
import { scheduleTokenRefresh } from '@/lib/auth/token-refresh-scheduler';
import { useAuthStore } from '@/stores/use-auth-store';
import { LoginIllustration } from './login-illustration';
import { LegalModal, type LegalKind } from './legal-modal';
import styles from './login.module.css';

const root = cva(styles.root);

type Step = 'resume' | 'email' | 'otp';
type Connection = 'google-oauth2' | 'apple' | 'email';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_LEN = 6;

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.16v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.16C1.43 8.55 1 10.22 1 12s.43 3.45 1.16 4.93l3.68-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.16 7.07l3.68 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 814 1000"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 67 0 123 44.1 165.8 44.1 41 0 106.9-46.3 173.9-46.3zm-246.7-220c31.3-37.4 54.5-89.4 54.5-141.4 0-7.1-.6-14.3-1.9-20.1-51.9 2-113.7 34.6-149.9 78.6-28.5 32.4-55.1 84.4-55.1 137.2 0 7.7 1.3 15.5 1.9 18 3.2.6 8.4 1.3 13.6 1.3 46.5 0 105.1-30.6 136.9-73.6z" />
    </svg>
  );
}

/**
 * Resolve the OAuth redirect URI for this build. Delegates to the pure
 * `authRedirectUri` (unit-tested in auth0.test.ts), supplying the live
 * platform/env values. Desktop production uses the hosted `/auth/desktop`
 * bounce page (`VITE_DESKTOP_WEB_ORIGIN`); web uses the current origin.
 */
function redirectUri(): string {
  return authRedirectUri({
    isDesktop,
    isDev: import.meta.env.DEV,
    webOrigin: isDesktop
      ? (import.meta.env.VITE_DESKTOP_WEB_ORIGIN as string)
      : window.location.origin,
  });
}


/**
 * Unified sign-in card. Three steps:
 *
 * - **Resume**: when getLastEmail() returns a value on mount. One-click
 *   Continue → kicks off passwordless OTP for the saved address.
 * - **Email**: first-time flow. Email input + Next, Google + Apple below.
 *   Next sends OTP and transitions to the OTP step.
 * - **OTP**: 6 single-character boxes with auto-advance + smart paste +
 *   auto-submit on the 6th digit. Resend + Use-a-different-email controls.
 *
 * Platform behavior:
 *
 * - **Web**: passwordless OTP completes in-page (verifyEmailOtp → hydrate
 *   store → router navigates to /). Social flow stashes PKCE state in
 *   sessionStorage and redirects; AuthCallback.tsx finishes the exchange.
 * - **Desktop (Tauri)**: passwordless OTP same as web. Social flow opens
 *   the system browser; the oauth-callback Rust event triggers the
 *   in-component listener below to exchange tokens via the Tauri CORS
 *   bypass commands.
 *
 * `connection=google-oauth2` / `connection=apple` skip Auth0's hosted page
 * and redirect straight to the provider — the whole reason for the setup.
 */
function SignInCard() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [pendingFor, setPendingFor] = useState<Connection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef<PendingAuth | null>(null);

  // On mount, check for a saved last-email; promote to resume step if present.
  useEffect(() => {
    let cancelled = false;
    void getLastEmail().then((saved) => {
      if (!cancelled && saved) {
        setEmail(saved);
        setStep('resume');
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Shared post-exchange handler used by both social and passwordless paths.
  async function handleSuccessfulExchange(tokens: OAuthTokens): Promise<void> {
    const userInfo = await fetchUserInfo(tokens.access_token);
    await setAccessTokenOnService(tokens.access_token);
    scheduleTokenRefresh(tokens.expires_in);
    if (tokens.refresh_token) {
      await storeRefreshToken(tokens.refresh_token);
    }
    await storeLastEmail(userInfo.email);
    useAuthStore.getState().setFromAuth({
      user: {
        sub: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name ?? '',
        picture: userInfo.picture ?? '',
      },
      accessToken: tokens.access_token,
      isAuthenticated: true,
      isLoading: false,
    });
  }

  // Desktop-only: listen for the `oauth-callback` event the Rust deep-link
  // handler emits when the system browser hands control back via nula://.
  useEffect(() => {
    if (!isDesktop) return;

    let unlisten: (() => void) | null = null;
    let mounted = true;
    void (async () => {
      try {
        const fn = await events.oauthCallback.listen(async (event) => {
          if (!mounted) return;
          const { code, state, error: oauthError } = parseCallback(event.payload.url);
          if (oauthError) {
            setError(`Sign-in failed: ${oauthError}`);
            setPendingFor(null);
            return;
          }
          if (!code || !state) {
            setError('Missing OAuth callback params.');
            setPendingFor(null);
            return;
          }
          const stash = pendingRef.current;
          if (!stash) {
            setError('No sign-in in progress. Try again.');
            setPendingFor(null);
            return;
          }
          if (state !== stash.state) {
            setError('State mismatch — possible CSRF. Try again.');
            setPendingFor(null);
            return;
          }
          try {
            const tokens = await exchangeCodeForTokens(code, stash.codeVerifier, redirectUri());
            await handleSuccessfulExchange(tokens);
            pendingRef.current = null;
            setPendingFor(null);
          } catch (err) {
            console.error('[SignInCard] token exchange failed:', err);
            setError(err instanceof Error ? err.message : 'Sign-in failed.');
            setPendingFor(null);
          }
        });
        unlisten = fn;
      } catch (err) {
        console.error('[SignInCard] Failed to register oauth-callback listener:', err);
      }
    })();
    return () => {
      mounted = false;
      unlisten?.();
    };
  }, []);

  async function beginSocialSignIn(connection: 'google-oauth2' | 'apple') {
    setError(null);
    setPendingFor(connection);
    try {
      const { url, pending } = await startAuth(redirectUri(), connection);
      pendingRef.current = pending;
      if (isDesktop && !import.meta.env.DEV) {
        // Production desktop: open system browser, wait for nula:// deep link.
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await openUrl(url);
      } else {
        // Web + desktop dev: same-tab redirect so sessionStorage is shared.
        sessionStorage.setItem('auth0_pending', JSON.stringify(pending));
        window.location.href = url;
      }
    } catch (err) {
      console.error('[SignInCard] Failed to start sign-in:', err);
      setError(err instanceof Error ? err.message : 'Could not start sign-in.');
      setPendingFor(null);
    }
  }

  async function sendOtpAndAdvance(targetEmail: string) {
    setError(null);
    setPendingFor('email');
    try {
      await startEmailPasswordless(targetEmail);
      setEmail(targetEmail);
      setStep('otp');
      setPendingFor(null);
    } catch (err) {
      console.error('[SignInCard] startEmailPasswordless failed:', err);
      setError(err instanceof Error ? err.message : 'Could not send sign-in code.');
      setPendingFor(null);
    }
  }

  async function submitOtp(otp: string) {
    setError(null);
    setPendingFor('email');
    try {
      const tokens = await verifyEmailOtp(email, otp);
      await handleSuccessfulExchange(tokens);
    } catch (err) {
      console.error('[SignInCard] verifyEmailOtp failed:', err);
      // Auth0 prefixes invalid_grant errors; strip our prefix so the
      // user-facing message reads like 'Wrong code.' rather than
      // 'OTP verification failed: Wrong code.'.
      const msg = err instanceof Error ? err.message.replace('OTP verification failed: ', '') : 'Sign-in failed.';
      setError(msg);
      setPendingFor(null);
    }
  }

  const anyPending = pendingFor !== null;

  if (step === 'resume') {
    return (
      <ResumeStep
        email={email}
        pending={anyPending}
        pendingFor={pendingFor}
        error={error}
        onContinue={() => void sendOtpAndAdvance(email)}
        onUseDifferent={() => { setEmail(''); setError(null); setStep('email'); }}
        google={() => void beginSocialSignIn('google-oauth2')}
        apple={() => void beginSocialSignIn('apple')}
      />
    );
  }
  if (step === 'otp') {
    return (
      <OtpStep
        email={email}
        pending={anyPending}
        error={error}
        onSubmit={(code) => void submitOtp(code)}
        onResend={() => void sendOtpAndAdvance(email)}
        onBack={() => { setError(null); setEmail(''); setStep('email'); }}
      />
    );
  }
  return (
    <EmailStep
      pending={anyPending}
      pendingFor={pendingFor}
      error={error}
      onSubmit={(addr) => void sendOtpAndAdvance(addr)}
      google={() => void beginSocialSignIn('google-oauth2')}
      apple={() => void beginSocialSignIn('apple')}
    />
  );
}

// ── EmailStep — first-time email + social ───────────────────────────────────

interface SocialActionsProps {
  google: () => void;
  apple: () => void;
  pendingFor: Connection | null;
  disabled: boolean;
}

function SocialActions({ google, apple, pendingFor, disabled }: SocialActionsProps) {
  return (
    <div className={styles.socialButtons}>
      <Button
        size="lg"
        block
        variant="default"
        pressEffect="glow"
        loading={pendingFor === 'google-oauth2'}
        disabled={disabled}
        onClick={google}
        icon={pendingFor === 'google-oauth2' ? undefined : <GoogleIcon />}
        className={`${styles.socialButton} ${styles.googleButton}`}
      >
        Continue with Google
      </Button>
      <Button
        size="lg"
        block
        variant="default"
        pressEffect="glow"
        loading={pendingFor === 'apple'}
        disabled={disabled}
        onClick={apple}
        icon={pendingFor === 'apple' ? undefined : <AppleIcon />}
        className={`${styles.socialButton} ${styles.appleButton}`}
      >
        Continue with Apple
      </Button>
    </div>
  );
}

interface EmailStepProps {
  pending: boolean;
  pendingFor: Connection | null;
  error: string | null;
  onSubmit: (email: string) => void;
  google: () => void;
  apple: () => void;
}

function EmailStep({ pending, pendingFor, error, onSubmit, google, apple }: EmailStepProps) {
  const [value, setValue] = useState('');
  const valid = EMAIL_RE.test(value);
  return (
    <div className={styles.cardBox}>
      <div className={styles.card}>
        <header className={styles.header}>
          <Text className={styles.eyebrow}>Welcome to Nula</Text>
          <Text className={styles.headerTitle}>
            Your calm <em className={styles.titleEm}>AI workspace.</em>
          </Text>
          <Text className={styles.headerSubtitle}>Sign in or create an account to pick up where you left off.</Text>
        </header>

        <div className={styles.emailField}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            label="Your email address"
            size="lg"
            variant="default"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid && !pending) onSubmit(value); }}
            className={styles.emailInput}
          />
          <Button
            size="lg"
            block
            variant="primary"
            pressEffect="glow"
            loading={pendingFor === 'email'}
            disabled={!valid || pending}
            onClick={() => onSubmit(value)}
            className={styles.socialButton}
          >
            Continue with email
          </Button>
        </div>

        <div className={styles.divider}>
          <span className={styles.dividerLine} aria-hidden />
          <span className={styles.dividerText}>or</span>
          <span className={styles.dividerLine} aria-hidden />
        </div>

        <SocialActions google={google} apple={apple} pendingFor={pendingFor} disabled={pending} />

        {error && (
          <Text type="danger" className={styles.error}>{error}</Text>
        )}
      </div>
    </div>
  );
}

// ── ResumeStep — "Last signed in as <email>" one-click resume ─────────────

interface ResumeStepProps {
  email: string;
  pending: boolean;
  pendingFor: Connection | null;
  error: string | null;
  onContinue: () => void;
  onUseDifferent: () => void;
  google: () => void;
  apple: () => void;
}

function ResumeStep({ email, pending, pendingFor, error, onContinue, onUseDifferent, google, apple }: ResumeStepProps) {
  return (
    <div className={styles.cardBox}>
      <div className={styles.card}>
        <header className={styles.header}>
          <Text className={styles.eyebrow}>Welcome back</Text>
          <Text className={styles.headerTitle}>
            Pick up where you <em className={styles.titleEm}>left off.</em>
          </Text>
        </header>

        <div className={styles.resumeEmailBox}>
          <Text className={styles.resumeLabel}>Last signed in as</Text>
          <Text className={styles.resumeEmail}>{email}</Text>
        </div>

        <Button
            size="lg"
          block
          variant="primary"
          pressEffect="glow"
          loading={pendingFor === 'email'}
          disabled={pending}
          onClick={onContinue}
          className={styles.socialButton}
        >
          Continue
        </Button>

        <button type="button" className={styles.backLink} onClick={onUseDifferent} disabled={pending}>
          Use a different email
        </button>

        <div className={styles.divider}>
          <span className={styles.dividerLine} aria-hidden />
          <span className={styles.dividerText}>or</span>
          <span className={styles.dividerLine} aria-hidden />
        </div>

        <SocialActions google={google} apple={apple} pendingFor={pendingFor} disabled={pending} />

        {error && (
          <Text type="danger" className={styles.error}>{error}</Text>
        )}
      </div>
    </div>
  );
}

// ── OtpStep — six-box code entry with auto-submit + paste + provider link ─

interface OtpStepProps {
  email: string;
  pending: boolean;
  error: string | null;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
}

function OtpStep({ email, pending, error, onSubmit, onResend, onBack }: OtpStepProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LEN).fill(''));
  const boxRefs = useRef<Array<HTMLInputElement | null>>(Array(OTP_LEN).fill(null));
  const provider: MailProvider | null = mailProviderFor(email);
  const lastFiredRef = useRef<string>('');

  // Auto-submit when all 6 boxes are filled. Guard against re-firing on the
  // same code (e.g. after the parent's error message comes back and React
  // re-renders with the same digits state).
  useEffect(() => {
    const code = digits.join('');
    if (code.length === OTP_LEN && /^\d{6}$/.test(code) && code !== lastFiredRef.current) {
      lastFiredRef.current = code;
      onSubmit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits.join('')]);

  function setDigitAt(idx: number, val: string) {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  }

  function onChange(idx: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setDigitAt(idx, digit);
    if (digit && idx < OTP_LEN - 1) {
      boxRefs.current[idx + 1]?.focus();
    }
  }

  function onKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      boxRefs.current[idx - 1]?.focus();
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LEN);
    if (pasted.length === 0) return;
    e.preventDefault();
    const next = Array(OTP_LEN).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    boxRefs.current[Math.min(pasted.length, OTP_LEN - 1)]?.focus();
  }

  function openProviderInbox() {
    if (!provider) return;
    if (isDesktop) {
      void import('@tauri-apps/plugin-opener').then(({ openUrl }) => openUrl(provider.url));
    } else {
      window.open(provider.url, '_blank', 'noopener,noreferrer');
    }
  }

  const allFilled = digits.every((d) => /^\d$/.test(d));

  return (
    <div className={styles.cardBox}>
      <div className={styles.card}>
        <header className={styles.header}>
          <Text className={styles.headerTitle}>Verify your email</Text>
          <Text className={styles.headerSubtitle}>Code has been sent to {email}</Text>
        </header>

        <div className={styles.otpRow} onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { boxRefs.current[i] = el; }}
              type="text"
              role="textbox"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => onChange(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className={styles.otpBox}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {provider && (
          <Button
            size="lg"
            block
            variant="default"
            onClick={openProviderInbox}
            className={styles.providerButton}
            disabled={pending}
          >
            {provider.label} <span aria-hidden>↗</span>
          </Button>
        )}

        <button type="button" className={styles.backLink} onClick={onResend} disabled={pending}>
          Didn't receive the code? Resend
        </button>

        <Button
            size="lg"
          block
          variant="primary"
          pressEffect="glow"
          loading={pending}
          disabled={!allFilled || pending}
          onClick={() => onSubmit(digits.join(''))}
          className={styles.socialButton}
        >
          Verify Code
        </Button>

        <button type="button" className={styles.backLink} onClick={onBack} disabled={pending}>
          ← Use a different email
        </button>

        {error && (
          <Text type="danger" className={styles.error}>{error}</Text>
        )}
      </div>
    </div>
  );
}

/**
 * Login route — full-bleed two-panel layout: the unified <SignInCard /> on the
 * left, a calm rounded illustration panel on the right. Web and desktop render
 * the same card; platform branches are internal to SignInCard. Terms / Privacy
 * open an in-app modal rather than navigating away.
 */
export function Login() {
  const [legal, setLegal] = useState<LegalKind | null>(null);

  return (
    <div className={root()} data-tauri-drag-region>
      {/* The whole login surface drags the frameless desktop window. The form
          and the footer legal links opt out so their controls stay clickable. */}
      <div className={styles.formPanel} data-tauri-drag-region>
        <motion.div
          className={styles.brand}
          data-tauri-drag-region
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <NulaLockup height={32} />
        </motion.div>

        <motion.div
          className={styles.signInWrapper}
          data-tauri-drag-region={false}
          initial={{ opacity: 0, y: 12, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2, type: 'spring', stiffness: 100, damping: 20 }}
        >
          <SignInCard />
        </motion.div>

        <div className={styles.footer} data-tauri-drag-region>
          <Text type="secondary" className={styles.footerLine} data-tauri-drag-region={false}>
            By continuing you agree to our{' '}
            <button type="button" className={styles.legalLink} onClick={() => setLegal('terms')}>Terms</button>
            {' & '}
            <button type="button" className={styles.legalLink} onClick={() => setLegal('privacy')}>Privacy</button>.
          </Text>
          <Text type="secondary" className={styles.footerOrg}>Nula Labs, Inc</Text>
        </div>
      </div>

      {/* Right — calm rounded illustration panel (already a drag region) */}
      <LoginIllustration />

      <LegalModal kind={legal} onClose={() => setLegal(null)} />
    </div>
  );
}
